/**
 * useAnalyticsTransactions Hook
 * 
 * Fetches transactions for Analytics page.
 * Handles loading state and error handling.
 */

import { useEffect, useState } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  Timestamp,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { getActivityTransactionsSeed } from "../../../utils/activityTransactionsSeed";
import { Transaction, TransactionCustomFieldValues } from "../../../types";

export interface TransactionWithDate extends Omit<Transaction, "date"> {
  date: Date;
}

export interface UseAnalyticsTransactionsParams {
  workspaceId: string;
}

export interface UseAnalyticsTransactionsResult {
  transactions: TransactionWithDate[];
  loading: boolean;
  setTransactions: React.Dispatch<React.SetStateAction<TransactionWithDate[]>>;
}

// Cache for transactions per workspace - persists across component unmounts
const transactionsCache = new Map<string, {
  transactions: TransactionWithDate[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching transactions for Analytics
 * Uses caching to avoid reloading data when navigating back to Analytics
 */
export const useAnalyticsTransactions = ({
  workspaceId,
}: UseAnalyticsTransactionsParams): UseAnalyticsTransactionsResult => {
  // Check cache first; if none, use Activity seed so first paint is instant when coming from Activity
  const cachedData = transactionsCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  const seedFromActivity = getActivityTransactionsSeed(workspaceId);
  const initialTransactions =
    hasValidCache && cachedData
      ? cachedData.transactions
      : (seedFromActivity as TransactionWithDate[] | null) ?? [];
  const skipLoadingSpinner = hasValidCache || (seedFromActivity !== null && seedFromActivity.length > 0);

  const [transactions, setTransactions] = useState<TransactionWithDate[]>(initialTransactions);
  const [loading, setLoading] = useState<boolean>(!skipLoadingSpinner);

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) {
      setLoading(false);
      return;
    }

    // Check cache again inside effect to ensure we have latest state
    const cachedData = transactionsCache.get(workspaceId);
    const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
    const seedFromActivity = getActivityTransactionsSeed(workspaceId);
    const hasSeed = seedFromActivity !== null && seedFromActivity.length > 0;

    // Only show loading spinner if we have no cache and no seed (e.g. direct nav to Analytics)
    if (!hasValidCache && !hasSeed) {
      setLoading(true);
    }

    const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
    const q = query(transactionsRef, orderBy("createdAt", "desc"));

    const processTransaction = (docSnap: DocumentSnapshot): TransactionWithDate => {
      const data = docSnap.data();
      if (!data) {
        throw new Error("Transaction data is missing");
      }

      let dateValue: Date;
      if (data.date && typeof (data.date as Timestamp).toDate === "function") {
        dateValue = (data.date as Timestamp).toDate();
      } else if (data.date instanceof Date) {
        dateValue = data.date;
      } else if (typeof data.date === "number") {
        dateValue = new Date(data.date);
      } else if (data.date && typeof data.date === "object" && "seconds" in data.date) {
        const timestampObj = data.date as { seconds: number };
        dateValue = new Date(timestampObj.seconds * 1000);
      } else if (typeof data.date === "string") {
        dateValue = new Date(data.date);
      } else {
        dateValue = new Date();
      }

      const transactionType =
        (data.type as "income" | "expense" | "transfer" | "adjustment") ||
        (data.isIncome ? "income" : "expense");

      return {
        id: docSnap.id,
        userId: data.userId as string,
        type: transactionType,
        amount: data.amount as number,
        currency: data.currency as string | undefined,
        date: dateValue,
        note: data.note as string | undefined,
        createdAt: data.createdAt,
        title: data.title as string | undefined,
        category: data.category as string | undefined,
        isIncome: data.isIncome as boolean | undefined,
        accountId: data.accountId as string | undefined,
        accountName: data.accountName as string | undefined,
        fromAccountId: data.fromAccountId as string | undefined,
        toAccountId: data.toAccountId as string | undefined,
        fromAccountName: data.fromAccountName as string | undefined,
        toAccountName: data.toAccountName as string | undefined,
        amountInDefaultCurrency: data.amountInDefaultCurrency as number | undefined,
        defaultCurrencyAtTime: data.defaultCurrencyAtTime as string | undefined,
        defaultCurrencyRate: data.defaultCurrencyRate as number | undefined,
        details: data.details as Record<string, string | number> | undefined,
        attachmentUrl: data.attachmentUrl as string | undefined,
        attachmentName: data.attachmentName as string | undefined,
        attachmentType: data.attachmentType as string | undefined,
        customFieldValues: data.customFieldValues as TransactionCustomFieldValues | undefined,
        isInitialBalance: data.isInitialBalance as boolean | undefined,
        friendlyIdSequence: data.friendlyIdSequence as number | undefined,
      };
    };

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const transactionsList: TransactionWithDate[] = snapshot.docs.map(processTransaction);
      
      // Update cache
      transactionsCache.set(workspaceId, {
        transactions: transactionsList,
        timestamp: Date.now(),
      });
      
      setTransactions(transactionsList);
      setLoading(false);
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching transactions:", error);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  return {
    transactions,
    loading,
    setTransactions,
  };
};
